'use client';

import * as React from 'react';
import { RippleButton, type RippleButtonProps } from '@/components/animate-ui/buttons/ripple';
import { FlipButton, type FlipButtonProps } from '@/components/animate-ui/buttons/flip';
import { LiquidButton, type LiquidButtonProps } from '@/components/animate-ui/buttons/liquid';
import { Button, buttonVariants } from '@/components/ui/button';
import { type VariantProps } from 'class-variance-authority';

type ButtonProps = React.ComponentProps<"button"> & VariantProps<typeof buttonVariants> & { asChild?: boolean };
type AnimationType = 'ripple' | 'liquid' | 'none';

interface AnimatedButtonProps extends Omit<ButtonProps, 'asChild'> {
  animation?: AnimationType;
  children: React.ReactNode;
  // Props specific to different animations
  rippleProps?: Partial<RippleButtonProps>;
  liquidProps?: Partial<LiquidButtonProps>;
}

interface FlipButtonWrapperProps extends Omit<ButtonProps, 'asChild'> {
  animation: 'flip';
  frontText: string;
  backText: string;
  children?: never; // Flip button doesn't use children
  flipProps?: Partial<FlipButtonProps>;
}

const AnimatedButton = React.forwardRef<HTMLButtonElement, AnimatedButtonProps>(
  ({ 
    animation = 'ripple', 
    children, 
    rippleProps, 
    liquidProps,
    ...props 
  }, ref) => {
    // Convert standard Button props to animated button props
    const convertProps = (originalProps: any) => {
      const { asChild, ...rest } = originalProps;
      return rest;
    };

    switch (animation) {
      case 'ripple':
        return (
          <RippleButton
            ref={ref}
            {...convertProps(props)}
            {...rippleProps}
          >
            {children}
          </RippleButton>
        );
      
      case 'liquid':
        return (
          <LiquidButton
            ref={ref}
            {...convertProps(props)}
            {...liquidProps}
          >
            {children}
          </LiquidButton>
        );
      
      default:
        return (
          <Button ref={ref} {...props}>
            {children}
          </Button>
        );
    }
  }
);

// Separate component for flip buttons since they have different props
const FlipAnimatedButton = React.forwardRef<HTMLButtonElement, FlipButtonWrapperProps>(
  ({ 
    frontText,
    backText,
    flipProps,
    ...props 
  }, ref) => {
    const convertProps = (originalProps: any) => {
      const { asChild, animation, ...rest } = originalProps;
      return rest;
    };

    return (
      <FlipButton
        ref={ref}
        frontText={frontText}
        backText={backText}
        {...convertProps(props)}
        {...flipProps}
      />
    );
  }
);

AnimatedButton.displayName = 'AnimatedButton';
FlipAnimatedButton.displayName = 'FlipAnimatedButton';

export { AnimatedButton, FlipAnimatedButton, type AnimatedButtonProps, type FlipButtonWrapperProps };
export type { AnimationType }; 